<template>
  <div class="group relative z-0 w-full" :class="{ 'mb-6': !hasError }">
    <input
      :id="inputId"
      v-bind="$attrs"
      class="peer block w-full appearance-none border-0 border-b-2 bg-transparent px-0 py-2.5 text-sm font-semibold focus:border-primaryLight focus:outline-none focus:ring-0"
      placeholder=" "
      :value="value"
      :type="type"
      :name="name"
      :autocomplete="autocomplete || undefined"
      :class="[
        hasError ? '!border-error !text-primary' : 'text-primary',
        inputClasses,
        disabledClasses,
        themes[theme],
      ]"
      :disabled="disabled"
      @input="$emit('update:value', $event.target.value)"
    />

    <BaseShowPassword v-if="toggleShowButton" :name="inputId" />

    <slot />

    <label
      v-bind="dynamicForAttr"
      class="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-100 transform text-xs font-semibold uppercase tracking-wider duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-100 md:text-sm"
      :class="[disabled ? 'opacity-25' : '', themes[theme]]"
    >
      {{ label }}
      <span v-if="isRequired" class="!text-[#eb5757]" :class="themes[theme]"
        >*</span
      >
    </label>

    <BaseErrorMessage v-if="hasError">
      <div class="inline-flex items-center space-x-2">
        <inline-svg
          :src="useAsset(`icons/base/error.svg`)"
          loading="lazy"
          alt="error-icon"
        />
        <span>
          {{ $t(errorMsg) }}
        </span>
      </div>
    </BaseErrorMessage>

    <slot name="errors"></slot>
  </div>
</template>

<script setup>
import InlineSvg from 'vue-inline-svg'

defineEmits(['update:value'])

const props = defineProps({
  value: {
    type: [String, Number, null],
    required: true,
  },
  type: {
    type: [String],
    default: 'text',
  },
  id: {
    type: [String],
    default: '',
  },
  name: {
    type: [String],
    default: '',
  },
  autocomplete: {
    type: [String],
    default: '',
  },
  label: {
    type: [String],
    default: '',
  },
  disabled: {
    type: [Boolean],
    default: false,
  },
  anyDirty: {
    type: [Boolean],
    default: true,
  },
  errors: {
    type: [Object],
    default: null,
  },
  inputClasses: {
    type: [String],
    default: '',
  },
  isRequired: {
    type: [Boolean],
    default: false,
  },
  toggleShowButton: {
    type: [Boolean],
    default: false,
  },
  theme: {
    type: [String],
    default: 'default',
    validator(value) {
      return ['default', 'white'].includes(value)
    },
  },
})

const inputId = computed(() => `${props.name}-input`)

const disabledClasses = computed(() => {
  return props.disabled ? 'opacity-25' : ''
})

const dynamicForAttr = computed(() => {
  return { for: inputId.value }
})

const hasError = computed(() => {
  return props.anyDirty && props.errors && props.errors?.[props.name]
})

const errorMsg = computed(() => {
  return props.errors[props.name] instanceof Array
    ? props.errors[props.name][0]
    : props.errors[props.name]
})

const themes = reactive({
  default: 'border-darkestGray text-darkestGray',
  white: 'border-white text-white',
})
</script>

<style lang="scss" scoped>
input:-webkit-autofill + label {
  @apply z-0;
}
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-background-clip: text;
  -webkit-text-fill-color: theme('colors.primary');
  transition: none;
  box-shadow: none;
}
.text-white {
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-text-fill-color: white;
  }
}
</style>
